/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, filter } from 'rxjs/operators';
import { submittalsActions } from './submittals.actions';
import { submittalsSelectors } from './submittals.selectors';
import {
  SubmittalRegistry,
  SubmittalPackageListingItem,
  SubmittalPackageResp,
  SUBMITTAL_PACKAGE_STATUS,
} from './submittals.types';
import { NotificationV2Service } from '@app/notification-v2.service';
import { SubmittalsApiService } from '@app/services/submittals-api.service';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';
import { submittalEntrySidebarActions } from '@app/framework/commitments/sidebar/submittal-entry-sidebar/submittal-entry-sidebar.actions';
import { submittalPackageSidebarActions } from '@app/framework/commitments/sidebar/submittal-package-sidebar/submittal-package-sidebar.actions';
import { submittalRegistryListingActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-registry-listing/submittal-registry-listing.actions';
import { submittalPackageDetailsActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-details.actions';
import { DriveApiService } from '@app/services/drive-api.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { DriveService } from '@app/services/drive.service';
import { CurrentUserService } from '@app/services/current-user.service';
import { TAB_OPTIONS } from '@app/framework/underline-tabs/underline-tabs.types';

@Injectable()
export class SubmittalsEffects {
  private actions$ = inject(Actions);
  private submittalsApiService = inject(SubmittalsApiService);
  private notif = inject(NotificationV2Service);
  private store = inject(Store);
  private driveApiService = inject(DriveApiService);
  private driveService = inject(DriveService);
  private interactionBarService = inject(InteractionBarStateService);
  private userService = inject(CurrentUserService);

  loadSubmittalRegistriesWrapper = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        viewProjectActions.refreshNeeded,
        submittalPackageSidebarActions.submittalRegistryListInitialized,
        submittalRegistryListingActions.submittalRegistryPageOpened,
        submittalRegistryListingActions.submittalRegistryFiltersChanged,
        submittalsActions.createRevisionForRegistryItemsFailure,
        submittalsActions.createRevisionForRegistryItemsSuccess,
        submittalsActions.deleteSubmittalRegistryItemSuccess,
        submittalsActions.deleteSubmittalRegistryItemFailure,
        submittalsActions.bulkAssignRegistryOwnerSuccess,
        submittalsActions.bulkAssignRegistryOwnerFailure,
        submittalsActions.updatePackageRegistryEntrySuccess,
        submittalsActions.updatePackageRegistryEntryFailure,
      ),
      concatLatestFrom(() => [
        this.store.select(submittalsSelectors.selectSubmittalsState),
        this.store.select(viewProjectSelectors.selectProjectId),
      ]),
      filter(() => this.userService.isPremiumSP),
      map(([action, state, projectId]) => {
        if ('projectId' in action) {
          projectId = action.projectId;
        }

        if (!projectId) {
          return submittalsActions.cancel();
        }

        const filters =
          'filters' in action
            ? { ...state.registriesFilters, ...action.filters }
            : {
                ...state.registriesFilters,
                page: 1,
              };

        return submittalsActions.loadSubmittalRegistryItems({
          projectId: projectId,
          filters: filters,
        });
      }),
    );
  });

  loadSubmittalRegistries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.loadSubmittalRegistryItems),
      switchMap((action) => {
        return this.submittalsApiService
          .loadSubmittalRegistries(action.projectId, action.filters)
          .pipe(
            map((data: SubmittalRegistry[]) => {
              return submittalsActions.loadSubmittalRegistriesSuccess({
                registries: data,
                filters: action.filters,
              });
            }),
            catchError((err) => {
              this.notif.showError(err?.error?.message ?? 'Failed to load submittal registries');
              console.error(err);
              return of(
                submittalsActions.loadSubmittalRegistriesFailure({
                  error: err?.error?.message ?? err?.message ?? 'Unknown error',
                }),
              );
            }),
          );
      }),
    );
  });

  loadNextSubmittalRegistryPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        submittalRegistryListingActions.loadNextSubmittalRegistryPage,
        submittalPackageSidebarActions.submittalRegistryListEndReached,
      ),
      debounceTime(200),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectSubmittalsState)),
      map(([action, state]) => {
        if (state.registriesReachedEnd) {
          return submittalsActions.cancel();
        }
        const page = (state.registriesFilters.page ?? 1) + 1;
        return submittalRegistryListingActions.submittalRegistryFiltersChanged({
          projectId: action.projectId,
          filters: { page },
        });
      }),
    );
  });

  loadSubmittalPackages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        viewProjectActions.refreshNeeded,
        submittalsActions.submittalPackageListOpened,
        submittalsActions.packageListFiltersChanged,
      ),
      concatLatestFrom(() => [
        this.store.select(submittalsSelectors.selectSubmittalsState),
        this.store.select(viewProjectSelectors.selectProjectId),
      ]),
      switchMap(([action, state, projectId]) => {
        const filters =
          'filters' in action
            ? { ...state.packagesListFilters, ...action.filters }
            : {
                ...state.packagesListFilters,
                page: 1,
              };

        if ('projectId' in action) {
          projectId = action.projectId;
        }

        if (!projectId) {
          return of(submittalsActions.cancel());
        }

        return this.submittalsApiService.loadSubmittalPackages(projectId, filters).pipe(
          map((data: SubmittalPackageListingItem[]) => {
            return submittalsActions.loadPackageListSuccess({ packages: data, filters: filters });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to load Submittal Package Log');
            console.error(err);
            return of(
              submittalsActions.loadPackageListFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  loadNextPackagePage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.loadNextPackagePage),
      debounceTime(200),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectSubmittalsState)),
      map(([action, state]) => {
        if (state.packagesReachedEnd) {
          return submittalsActions.cancel();
        }
        const page = (state.packagesListFilters.page ?? 1) + 1;
        return submittalsActions.packageListFiltersChanged({
          projectId: action.projectId,
          filters: { page },
        });
      }),
    );
  });

  loadPackageDetailsWrapper$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        submittalPackageDetailsActions.onPackageDetailsOpen,
        submittalPackageSidebarActions.editPackageOpened,
        submittalPackageDetailsActions.uploadFilesToCategorySuccess,
        submittalPackageDetailsActions.uploadFilesToCategoryFailure,
        submittalsActions.packageFileDeleteSuccess,
        submittalsActions.packageFileDeleteFailure,
        submittalsActions.packageFileCategoryDeleteSuccess,
        submittalsActions.packageFileCategoryDeleteFailure,
        submittalsActions.sendPackageForApprovalSuccess,
        submittalsActions.sendPackageForApprovalFailure,
        submittalsActions.createRevisionForPackageSuccess,
        submittalsActions.createRevisionForPackageFailure,
        submittalsActions.voidPackageSuccess,
        submittalsActions.voidPackageFailure,
        submittalPackageDetailsActions.newVersionSelected,
      ),
      map((action) => {
        return submittalsActions.loadPackageDetails({ packageId: action.packageId });
      }),
    );
  });

  loadPackageDetails = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.loadPackageDetails),
      switchMap((action) => {
        return this.submittalsApiService.loadPackageDetails(action.packageId).pipe(
          map((data) => {
            return submittalsActions.loadPackageDetailsSuccess({ packageDetails: data });
          }),
          catchError((err) => {
            console.error(err);
            this.notif.showError(err?.error?.message ?? 'Failed to load package details');
            return of(submittalsActions.loadPackageDetailsFailure());
          }),
        );
      }),
    );
  });

  updateSubmittalPackageStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.updateSubmittalPackageStatus),
      switchMap((action) => {
        return this.submittalsApiService.updatePackageStatus(action.packageId, action.status).pipe(
          map((response) => {
            this.notif.showSuccess('Package status updated successfully');
            return submittalsActions.updateSubmittalPackageStatusSuccess({
              submittalPackage: response,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to update package status');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageStatusFailure());
          }),
        );
      }),
    );
  });

  saveRegistryEntryFromPackageDetailsWrapper$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.updatePackageRegistryEntry),
      switchMap((action) => {
        return this.submittalsApiService
          .updateSubmittalRegistryEntryPatch(action.registryEntryId, action.registryEntryPayload)
          .pipe(
            map((responseData) => {
              this.notif.showSuccess('Entry note updated successfully');
              return submittalsActions.updatePackageRegistryEntrySuccess({
                registryEntry: responseData,
              });
            }),
            catchError((err) => {
              this.notif.showError(err?.error?.message ?? 'Failed to update entry note');
              console.error(err);
              return of(
                submittalsActions.updatePackageRegistryEntryFailure({
                  registryEntryId: action.registryEntryId,
                }),
              );
            }),
          );
      }),
    );
  });

  saveRegistryEntryFromPackageDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.saveEntryClicked),
      map((action) => {
        return submittalsActions.updatePackageRegistryEntry({
          registryEntryId: action.registryEntryId,
          registryEntryPayload: action.registryEntryPayload,
        });
      }),
    );
  });

  unlockRegistryEntryFromPackageDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.unlockEntryClicked),
      map((action) => {
        return submittalsActions.updatePackageRegistryEntry({
          registryEntryId: action.registryEntryId,
          registryEntryPayload: { is_locked: false },
        });
      }),
    );
  });

  deleteFileFromPackageDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.deleteFileClicked),
      switchMap((action) => {
        return from(this.driveApiService.deleteFilePromise(action.fileId)).pipe(
          map(() => {
            this.notif.showSuccess('File deleted successfully from package');
            return submittalsActions.packageFileDeleteSuccess({
              packageId: action.packageId,
              fileId: action.fileId,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to delete file from package');
            console.error(err);
            return of(
              submittalsActions.packageFileDeleteFailure({
                packageId: action.packageId,
                fileId: action.fileId,
              }),
            );
          }),
        );
      }),
    );
  });

  deleteAllFilesInCategoryFromPackageDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.deleteAllFilesInCategorySelected),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectPackageDetails)),
      switchMap(([action, packageDetails]) => {
        const filesToDelete = packageDetails?.files?.[action.documentCategory];
        if (!filesToDelete || filesToDelete.length === 0) {
          this.notif.showError('No files found in the selected category to delete');
          return of(
            submittalsActions.packageFileCategoryDeleteFailure({
              packageId: action.packageId,
            }),
          );
        }

        const deleteRequests = filesToDelete.map((file) =>
          from(this.driveApiService.deleteFilePromise(file.id)).pipe(
            catchError((err) => {
              return of(err);
            }),
          ),
        );
        const hash = this.notif.showLoading('Deleting files...');
        return forkJoin(deleteRequests).pipe(
          map((responses) => {
            this.notif.close(hash);

            const someFailed = responses.some((res) => res?.error);
            const allFailed = responses.every((res) => res?.error);
            if (someFailed || allFailed) {
              this.notif.showError(
                `Failed to delete ${allFailed ? 'the' : 'some'} files in the selected category`,
              );
              return submittalsActions.packageFileCategoryDeleteFailure({
                packageId: action.packageId,
              });
            }

            this.notif.showSuccess('All files deleted successfully.');
            return submittalsActions.packageFileCategoryDeleteSuccess({
              packageId: action.packageId,
            });
          }),
        );
      }),
    );
  });

  downloadAllFilesInCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.downloadAllFilesInCategorySelected),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectPackageDetails)),
      switchMap(([action, packageDetails]) => {
        const filesToDownload = packageDetails?.files?.[action.documentCategory];
        const categoryNameFormatted =
          action.documentCategory.charAt(0).toUpperCase() + action.documentCategory.slice(1);
        if (!filesToDownload || filesToDownload.length === 0) {
          this.notif.showError('No files found in the selected category to download');
          return of(submittalsActions.cancel());
        }
        return this.driveService
          .downloadMultipleFilesAsZip(
            filesToDownload,
            `Submittal_Package_${packageDetails.package_name}_${categoryNameFormatted}_documents`,
          )
          .pipe(map(() => submittalsActions.cancel()));
      }),
    );
  });

  requestSubmittals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.requestSubmittals),
      switchMap((action) => {
        return this.submittalsApiService.requestSubmittalsFromOwners(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Submittal request sent successfully');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to send submittal request');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  cancelRequestSubmittals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.cancelSubmittalRequest),
      switchMap((action) => {
        return this.submittalsApiService.cancelRequestSubmittalsFromOwners(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Submittal request cancelled.');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to send submittal request');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  createRevisionOnPackageDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.createRevision),
      switchMap((action) => {
        return this.submittalsApiService.createRevisionForPackage(action.packageId).pipe(
          map(() => {
            this.notif.showSuccess('Revision created successfully for the package');
            return submittalsActions.createRevisionForPackageSuccess({
              packageId: action.packageId,
            });
          }),
          catchError((err) => {
            this.notif.showError(
              err?.error?.message ?? 'Failed to create revision for the package',
            );
            console.error(err);
            return of(
              submittalsActions.createRevisionForPackageFailure({ packageId: action.packageId }),
            );
          }),
        );
      }),
    );
  });

  deletePackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.deletePackage),
      switchMap((action) => {
        return this.submittalsApiService.deletePackage(action.packageId).pipe(
          map(() => {
            this.notif.showSuccess('Package deleted successfully');
            return submittalsActions.deletePackageSuccess({ packageId: action.packageId });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to delete package');
            console.error(err);
            return of(submittalsActions.deletePackageFailure({ packageId: action.packageId }));
          }),
        );
      }),
    );
  });

  sendPackageForApproval$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.sendPackageForApproval),
      switchMap((action) => {
        return this.submittalsApiService.requestApproval(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Package sent for approval successfully');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to send package for approval');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  cancelPackageApproval$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.cancelPackageApproval),
      switchMap((action) => {
        return this.submittalsApiService.cancelRequestApproval(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Package approval cancelled');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to cancel package approval');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  markAsComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.markAsComplete),
      switchMap((action) => {
        return this.submittalsApiService.markAsComplete(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Package marked as complete');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to mark package as complete');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  markAsReady$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.markAsReady),
      switchMap((action) => {
        return this.submittalsApiService.markAsReady(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Package marked as ready');
            return submittalsActions.updateSubmittalPackageSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to mark package as ready');
            console.error(err);
            return of(submittalsActions.updateSubmittalPackageFailure(err));
          }),
        );
      }),
    );
  });

  submitToOwner$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.submitToOwner),
      switchMap((action) => {
        return this.submittalsApiService.submitPackageToOwners(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Package submitted to Owner successfully');
            return submittalsActions.submitPackageToOwnerSuccess({ submittalPackage: response });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to submit package to owner');
            return of(
              submittalsActions.submitPackageToOwnerFailure({ packageId: action.packageId }),
            );
          }),
        );
      }),
    );
  });

  cancelSubmitToOwner$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.cancelSubmitToOwner),
      switchMap((action) => {
        return this.submittalsApiService.cancelSubmitPackageToOwners(action.packageId).pipe(
          map((response) => {
            this.notif.showSuccess('Submit to Owner cancelled successfully');
            return submittalsActions.cancelSubmitPackageToOwnerSuccess({
              submittalPackage: response,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to submit package to owner');
            return of(
              submittalsActions.cancelSubmitPackageToOwnerFailure({ packageId: action.packageId }),
            );
          }),
        );
      }),
    );
  });

  voidPackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.voidPackage),
      map((action) => {
        return submittalsActions.updateSubmittalPackageStatus({
          packageId: action.packageId,
          status: SUBMITTAL_PACKAGE_STATUS.VOID,
        });
      }),
    );
  });

  closePackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.closePackage),
      map((action) => {
        return submittalsActions.updateSubmittalPackageStatus({
          packageId: action.packageId,
          status: SUBMITTAL_PACKAGE_STATUS.CLOSED,
        });
      }),
    );
  });

  reopenPackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.reopenPackage),
      map((action) => {
        return submittalsActions.updateSubmittalPackageStatus({
          packageId: action.packageId,
          status: SUBMITTAL_PACKAGE_STATUS.OPEN,
        });
      }),
    );
  });

  closePackageDetailsOnDeleteSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.deletePackageSuccess),
      map(() => {
        this.interactionBarService.close();
        return submittalsActions.cancel();
      }),
    );
  });

  transmittalLogAccessed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageDetailsActions.transmittalLogsAccessed),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectPackageDetails)),
      map(([action, submittalPackage]) => {
        return submittalsActions.loadTransmittalLogs({ packageId: submittalPackage.id });
      }),
    );
  });

  transmittalLogNeedsUpdate = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        submittalsActions.cancelSubmitPackageToOwnerSuccess,
        submittalsActions.submitPackageToOwnerSuccess,
        submittalsActions.updateSubmittalPackageSuccess,
      ),
      concatLatestFrom(() => this.store.select(submittalsSelectors.selectSubmittalsState)),
      // Only load transmittal logs if the transmittal logs is accessed, to avoid loading transmittal logs unnecessarily when the user hasn't even accessed them yet
      filter(([action, state]) => state.selectedPackageDetailsTab === TAB_OPTIONS.TRANSMITTAL_LOG),
      map(([action, state]) => {
        return submittalsActions.loadTransmittalLogs({ packageId: state?.packageDetails?.id });
      }),
    );
  });

  loadTransmittalLogs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.loadTransmittalLogs),
      switchMap((action) => {
        return this.submittalsApiService.getTransmittalLogs(action.packageId).pipe(
          map((transmittalLogs) => {
            return submittalsActions.loadTransmittalLogsSuccess({
              transmittalLogs,
              packageId: action.packageId,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to load transmittal logs');
            console.error(err);
            return of(
              submittalsActions.loadTransmittalLogsFailure({ packageId: action.packageId }),
            );
          }),
        );
      }),
    );
  });

  loadProjectInviteMembersOnEnterSubmittalSidebar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        submittalEntrySidebarActions.loadPage,
        submittalPackageSidebarActions.createPackageOpened,
      ),
      filter(() => !this.userService.isFreeSP),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      map(([_, projectId]) => {
        return viewProjectActions.loadProjectInviteMembers({ projectId });
      }),
    );
  });

  loadProjectTeamMembersOnEnterSubmittalPackageSidebar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalPackageSidebarActions.createPackageOpened),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      filter(() => !this.userService.isFreeSP),
      map(([_, projectId]) => {
        return viewProjectActions.loadProjectTeamMembers({ projectId });
      }),
    );
  });

  loadNextIdOnSubmittalEntrySidebar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalEntrySidebarActions.loadPage),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => {
        return this.submittalsApiService.getNextRegistryId(projectId).pipe(
          map((res) => {
            return submittalsActions.loadNextRegistryIdSuccess({ nextRegistryId: res.next_id });
          }),
          catchError((err) => {
            console.error(err);
            return of(
              submittalsActions.loadNextRegistryIdFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  createSubmittalRegistry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.createSubmittalRegistry),
      switchMap((payload) => {
        return this.submittalsApiService.createSubmittalRegistry(payload.registry).pipe(
          map((registry: SubmittalRegistry) => {
            this.notif.showSuccess('Submittal entry created successfully');
            return submittalsActions.createSubmittalRegistrySuccess({ registry });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to create submittal entry');
            console.error(err);
            return of(
              submittalsActions.createSubmittalRegistryFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  createRevisionForRegistryItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalRegistryListingActions.createRevisionForRegistryItems),
      switchMap((action) => {
        const requests = action.registryIds.map((id) =>
          this.submittalsApiService
            .createRevisionForRegistryItems(id)
            .pipe(catchError((err) => of(err))),
        );
        const hash = this.notif.showLoading(
          `Creating revision${requests.length > 0 ? 's' : ''}...`,
        );

        return forkJoin(requests).pipe(
          map((responses) => {
            this.notif.close(hash);
            const errorCount = responses.filter((response) => response?.error).length;
            const requestCount = responses.length;
            if (errorCount === 1) {
              this.notif.showError(`The revision failed to be created.`);
              return submittalsActions.createRevisionForRegistryItemsFailure();
            } else if (errorCount > 1) {
              this.notif.showError(
                `${errorCount} of ${requestCount} revision${requestCount > 1 ? 's' : ''} failed to be created.`,
              );
              return submittalsActions.createRevisionForRegistryItemsFailure();
            }
            this.notif.showSuccess(
              `${requestCount} revision${requestCount > 1 ? 's' : ''} created successfully.`,
            );
            return submittalsActions.createRevisionForRegistryItemsSuccess();
          }),
        );
      }),
    );
  });

  deleteSubmittalRegistryItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalRegistryListingActions.deleteSubmittalRegistryItem),
      switchMap((action) => {
        const requests = action.registryIds.map((id) =>
          this.submittalsApiService
            .deleteSubmittalRegistryItem(id)
            .pipe(catchError((err) => of(err))),
        );
        const hash = this.notif.showLoading(
          `Deleting registry item${requests.length > 0 ? 's' : ''}...`,
        );

        return forkJoin(requests).pipe(
          map((responses) => {
            this.notif.close(hash);
            const errorCount = responses.filter((response) => response?.error).length;
            const requestCount = responses.length;
            if (errorCount === 1) {
              this.notif.showError(`The selected registry item failed to be deleted.`);
              return submittalsActions.deleteSubmittalRegistryItemFailure();
            } else if (errorCount > 1) {
              this.notif.showError(
                `${errorCount} of ${requestCount} registry items failed to be deleted.`,
              );
              return submittalsActions.deleteSubmittalRegistryItemFailure();
            }

            this.notif.showSuccess('Selected registry items deleted successfully.');
            return submittalsActions.deleteSubmittalRegistryItemSuccess();
          }),
        );
      }),
    );
  });

  bulkAssignRegistryOwner$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalRegistryListingActions.assignOwnerToRegistries),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        return this.submittalsApiService
          .bulkAssignRegistryOwner({
            entry_ids: action.registryIds,
            owner_id: action.owner_id,
            project_id: projectId,
          })
          .pipe(
            map(() => {
              this.notif.showSuccess('Owner assigned successfully to selected registry items.');
              return submittalsActions.bulkAssignRegistryOwnerSuccess();
            }),
            catchError((err) => {
              this.notif.showError(
                err?.error?.message ?? 'Failed to assign owner to selected registry items',
              );
              console.error(err);
              return of(submittalsActions.bulkAssignRegistryOwnerFailure());
            }),
          );
      }),
    );
  });

  removePackageFromRegistry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalRegistryListingActions.removePackageFromRegistry),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        return this.submittalsApiService
          .bulkAssignRegistryOwner({
            entry_ids: action.registryIds,
            package_id: null,
            project_id: projectId,
          })
          .pipe(
            map(() => {
              this.notif.showSuccess('Package removed successfully from selected registry items.');
              return submittalsActions.bulkAssignRegistryOwnerSuccess();
            }),
            catchError((err) => {
              this.notif.showError(
                err?.error?.message ?? 'Failed to remove package from selected registry items',
              );
              console.error(err);
              return of(submittalsActions.bulkAssignRegistryOwnerFailure());
            }),
          );
      }),
    );
  });

  createSubmittalPackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.createSubmittalPackage),
      switchMap((payload) => {
        return this.submittalsApiService.createSubmittalPackage(payload.submittalPackage).pipe(
          map((submittalPackage: SubmittalPackageResp) => {
            this.notif.showSuccess('Submittal package created successfully');
            return submittalsActions.createSubmittalPackageSuccess({
              submittalPackage: submittalPackage,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to create submittal package');
            console.error(err);
            return of(
              submittalsActions.createSubmittalPackageFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  updateSubmittalPackage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submittalsActions.updateSubmittalPackage),
      switchMap((payload) => {
        if (!payload.submittalPackage.id) {
          return of(
            submittalsActions.updateSubmittalPackageFailure({
              error: 'Package ID is required for update',
            }),
          );
        }
        return this.submittalsApiService.updateSubmittalPackage(payload.submittalPackage).pipe(
          map((submittalPackage: SubmittalPackageResp) => {
            this.notif.showSuccess('Submittal package updated successfully');
            return submittalsActions.updateSubmittalPackageSuccess({
              submittalPackage: submittalPackage,
            });
          }),
          catchError((err) => {
            this.notif.showError(err?.error?.message ?? 'Failed to update submittal package');
            console.error(err);
            return of(
              submittalsActions.updateSubmittalPackageFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });
}
